import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SuperAdminSidebar from '../../../components/SuperAdminsidebar';
import { fetchRevenueDetails } from '../../../../services/superadmin_services/revenue_services';
import { downloadRevenueAttachment, triggerDownload } from '../../../../services/superadmin_services/download_attachment_service';

const RevenueDetailsPage = () => {
  const { revenueId } = useParams();
  const [revenue, setRevenue] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [downloadError, setDownloadError] = useState('');

  // Load revenue details
  const loadRevenueDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetchRevenueDetails(revenueId);
      setRevenue(response.data.revenue);
      setAttachments(response.data.attachments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle attachment download
  const handleDownload = async (attachmentId, title) => {
    try {
      setDownloadingIds(prev => new Set([...prev, attachmentId]));
      setDownloadError('');
      
      const response = await downloadRevenueAttachment(attachmentId);
      triggerDownload(response.blob, response.filename || title);
    } catch (err) {
      console.error('Download error:', err);
      setDownloadError(`Failed to download "${title}": ${err.message}`);
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(attachmentId);
        return newSet;
      });
    }
  };

  // Format currency in XAF (Cameroon Francs)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    if (revenueId) {
      loadRevenueDetails();
    }
  }, [revenueId]);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <SuperAdminSidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
              <p className="mt-2 text-gray-600">Loading revenue details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <SuperAdminSidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
            <div className="mt-4">
              <Link
                to="/superadmin/revenues"
                className="text-secondary-600 hover:text-secondary-800"
              >
                ← Back to Revenues
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <SuperAdminSidebar />
      
      <div className="flex-1 ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/superadmin/revenues"
              className="text-secondary-600 hover:text-secondary-800 mb-4 inline-block"
            >
              ← Back to Revenues
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Revenue Details</h1>
          </div>

          {/* Download Error Message */}
          {downloadError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
              {downloadError}
              <button
                onClick={() => setDownloadError('')}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Revenue Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <p className="text-gray-900">{revenue?.title}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <p className="text-gray-900 font-semibold text-lg">
                      {formatCurrency(revenue?.amount)}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Received</label>
                    <p className="text-gray-900">{formatDate(revenue?.date_received)}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client/Source</label>
                    <p className="text-gray-900">{revenue?.client || '-'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <p className="text-gray-900">{revenue?.payment_method || '-'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                    <p className="text-gray-900">{revenue?.reference_number || '-'}</p>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <p className="text-gray-900">
                      {revenue?.category_details?.name || '-'}
                    </p>
                    {revenue?.category_details?.description && (
                      <p className="text-sm text-gray-500 mt-1">
                        {revenue.category_details.description}
                      </p>
                    )}
                  </div>
                </div>
                
                {revenue?.description && (
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <p className="text-gray-900">{revenue.description}</p>
                  </div>
                )}
              </div>

              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Attachments ({attachments.length})
                  </h2>
                  
                  <div className="space-y-3">
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{attachment.title}</p>
                          <p className="text-sm text-gray-500">
                            {attachment.file_type?.toUpperCase()} • {formatFileSize(attachment.file_size)} • 
                            Uploaded {formatDate(attachment.upload_date)}
                          </p>
                        </div>
                        
                        <button
                          onClick={() => handleDownload(attachment.id, attachment.title)}
                          disabled={downloadingIds.has(attachment.id)}
                          className="ml-4 px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {downloadingIds.has(attachment.id) ? (
                            <span className="flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Downloading...
                            </span>
                          ) : (
                            'Download'
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - Owner Information */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Owner Information</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                    <p className="text-gray-900">
                      {revenue?.owner_details?.first_name} {revenue?.owner_details?.last_name}
                    </p>
                    <p className="text-sm text-gray-500">@{revenue?.owner_details?.username}</p>
                    <p className="text-sm text-gray-500">{revenue?.owner_details?.email}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created By</label>
                    <p className="text-gray-900">
                      {revenue?.created_by_details?.first_name} {revenue?.created_by_details?.last_name}
                    </p>
                    <p className="text-sm text-gray-500">@{revenue?.created_by_details?.username}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Created</label>
                    <p className="text-gray-900">{formatDate(revenue?.date_created)}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Modified</label>
                    <p className="text-gray-900">{formatDate(revenue?.date_modified)}</p>
                    {revenue?.last_modified_by_details && (
                      <p className="text-sm text-gray-500">
                        by {revenue.last_modified_by_details.first_name} {revenue.last_modified_by_details.last_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueDetailsPage;